// src/components/CountryDirectorsDashboard.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { FaCamera, FaUpload } from 'react-icons/fa';
import PublicLayout from './public/PublicLayout';

// API Configuration
const API_BASE = process.env.REACT_APP_API_URL;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!API_BASE || !BACKEND_URL) {
  throw new Error('API URLs not configured');
}

const CountryDirectorsDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboardData, setDashboardData] = useState({
    reports: [],
    events: [],
    projects: [],
    universities: [],
    implementingUniversities: []
  });
  const [directorProfile, setDirectorProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'view'
  const [modalType, setModalType] = useState(''); // 'addReport', 'addProject', 'uploadProfilePic', 'addUniversity', 'university'
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({});
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDirectorProfile();
    fetchCountryDirectorData();
  }, []);

  const fetchDirectorProfile = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        toast.error('Authentication required');
        navigate('/login');
        return;
      }

      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const response = await fetch(`${API_BASE}/admin/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data.admin) {
          setDirectorProfile(data.data.admin);
          console.log('Director Profile loaded:', data.data.admin);
        }
      } else {
        console.error('Failed to fetch profile:', response.status);
        toast.error('Failed to load profile');
      }
    } catch (error) {
      console.error('Error fetching director profile:', error);
      toast.error('Error loading profile');
    }
  };

  const fetchCountryDirectorData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        console.error('No token found');
        setLoading(false);
        return;
      }

      // Fetch actual reports from API
      const reportsResponse = await fetch(`${API_BASE}/leader-reports/reports`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (reportsResponse.ok) {
        const reportsData = await reportsResponse.json();
        console.log('Reports loaded:', reportsData);

        setDashboardData(prev => ({
          ...prev,
          reports: reportsData.data || []
        }));
      } else {
        console.error('Failed to fetch reports:', reportsResponse.status);
      }

      // Fetch projects from API
      const projectsResponse = await fetch(`${API_BASE}/projects/projects`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (projectsResponse.ok) {
        const projectsData = await projectsResponse.json();
        console.log('Projects loaded:', projectsData);

        setDashboardData(prev => ({
          ...prev,
          projects: projectsData.data || []
        }));
      } else {
        console.error('Failed to fetch projects:', projectsResponse.status);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching country director data:', error);
      setLoading(false);
    }
  };

  const openModal = (type) => {
    setModalMode('create');
    setModalType(type);
    setSelectedUser(null);
    setFormData({});
    setProfilePictureFile(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalMode('create');
    setModalType('');
    setSelectedUser(null);
    setFormData({});
    setProfilePictureFile(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        toast.error('Please login to continue');
        return;
      }

      if (modalType === 'uploadProfilePic') {
        // Upload profile picture
        if (!profilePictureFile) {
          toast.error('Please select an image file');
          return;
        }

        const uploadFormData = new FormData();
        uploadFormData.append('image', profilePictureFile);

        const response = await fetch(`${BACKEND_URL}/api/admin-upload/leadership/image`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: uploadFormData
        });

        if (response.ok) {
          const result = await response.json();
          console.log('Image upload result:', result);

          // Update profile picture in admin_users table via profile endpoint
          const updateResponse = await fetch(`${API_BASE}/admin/auth/profile/picture`, {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ profile_picture_url: result.imageUrl })
          });

          if (updateResponse.ok) {
            await fetchDirectorProfile(); // Refresh profile
            toast.success('Profile picture uploaded successfully!');
            // Force a re-render by updating state
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          } else {
            toast.error('Failed to update profile picture');
          }
          closeModal();
        } else {
          const errorData = await response.json();
          toast.error(errorData.message || 'Failed to upload profile picture');
        }
      } else if (modalType === 'addReport') {
        // Submit or update report
        if (modalMode === 'edit' && selectedUser) {
          // Update existing report
          const reportData = {
            title: formData.title,
            content: formData.description || formData.content,
            category_id: formData.category_id || 1,
            activity_date: formData.activity_date || new Date().toISOString().split('T')[0],
            tags: formData.tags || ''
          };

          const response = await fetch(`${API_BASE}/leader-reports/reports/${selectedUser.id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(reportData)
          });

          if (response.ok) {
            // Upload PDF if provided
            if (formData.pdfFile) {
              console.log('Uploading PDF for report ID:', selectedUser.id);
              console.log('PDF file:', formData.pdfFile);
              const pdfFormData = new FormData();
              pdfFormData.append('file', formData.pdfFile);

              console.log('FormData contents:', Array.from(pdfFormData.entries()));
              console.log('File details:', formData.pdfFile.name, formData.pdfFile.type, formData.pdfFile.size);

              // DO NOT set Content-Type header - let browser set it with boundary
              const uploadResponse = await fetch(`${API_BASE}/leader-reports/reports/${selectedUser.id}/attachments`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`
                },
                body: pdfFormData
              });

              if (uploadResponse.ok) {
                const uploadResult = await uploadResponse.json();
                console.log('Upload success:', uploadResult);
                toast.success('Report and PDF updated successfully!');
              } else {
                const uploadError = await uploadResponse.text(); // Get raw text first
                console.error('Upload failed - Status:', uploadResponse.status);
                console.error('Upload failed - Response:', uploadError);
                try {
                  const errorJson = JSON.parse(uploadError);
                  console.error('Upload failed - Parsed:', errorJson);
                  toast.error(`PDF upload failed: ${errorJson.message || 'Unknown error'}`);
                } catch (e) {
                  console.error('Could not parse error response');
                  toast.error(`PDF upload failed: ${uploadError}`);
                }
              }
            } else {
              toast.success('Report updated successfully!');
            }

            // Refresh reports list
            fetchCountryDirectorData();
            closeModal();
          } else {
            const error = await response.json();
            toast.error(error.message || 'Failed to update report');
          }
        } else {
          // Create new report
          const reportData = {
            title: formData.title,
            content: formData.description || formData.content,
            category_id: formData.category_id || 1, // Default category
            activity_date: formData.activity_date || new Date().toISOString().split('T')[0],
            status: 'draft',
            tags: formData.tags || ''
          };

          const response = await fetch(`${API_BASE}/leader-reports/reports`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(reportData)
          });

          if (response.ok) {
            const result = await response.json();
            const reportId = result.data.id || result.data.report_id;

            // Upload PDF if provided
            if (formData.pdfFile && reportId) {
              console.log('Uploading PDF for new report ID:', reportId);
              console.log('PDF file:', formData.pdfFile);
              const pdfFormData = new FormData();
              pdfFormData.append('file', formData.pdfFile);

              console.log('FormData contents:', Array.from(pdfFormData.entries()));

              // DO NOT set Content-Type header - let browser set it with boundary
              const uploadResponse = await fetch(`${API_BASE}/leader-reports/reports/${reportId}/attachments`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`
                  // Content-Type will be set automatically by browser
                },
                body: pdfFormData
              });

              if (uploadResponse.ok) {
                const uploadResult = await uploadResponse.json();
                console.log('Upload success:', uploadResult);
                toast.success('Report and PDF uploaded successfully!');
              } else {
                const uploadError = await uploadResponse.json();
                console.error('Upload failed:', uploadError);
                toast.error(`Report created but PDF upload failed: ${uploadError.message}`);
              }
            } else {
              console.log('No PDF file to upload or no report ID');
              toast.success('Report created successfully!');
            }

            // Refresh reports list
            fetchCountryDirectorData();
            closeModal();
          } else {
            const error = await response.json();
            toast.error(error.message || 'Failed to create report');
          }
        }
      } else if (modalType === 'addProject') {
        // Create or update project
        if (modalMode === 'edit' && selectedUser) {
          // Update existing project
          const projectData = {
            title: formData.title,
            description: formData.description,
            date_started: formData.date_started,
            status: formData.status || 'Started',
            expected_completion_date: formData.expected_completion_date,
            budget: formData.budget,
            target_beneficiaries: formData.target_beneficiaries,
            objectives: formData.objectives,
            region: formData.region,
            progress: formData.progress
          };

          const response = await fetch(`${API_BASE}/projects/projects/${selectedUser.id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(projectData)
          });

          if (response.ok) {
            toast.success('Project updated successfully!');
            fetchCountryDirectorData();
            closeModal();
          } else {
            const error = await response.json();
            toast.error(error.message || 'Failed to update project');
          }
        } else {
          // Create new project
          const projectData = {
            title: formData.title,
            description: formData.description,
            date_started: formData.date_started,
            status: formData.status || 'Started',
            expected_completion_date: formData.expected_completion_date,
            budget: formData.budget,
            target_beneficiaries: formData.target_beneficiaries,
            objectives: formData.objectives,
            country: directorProfile?.country,
            region: formData.region
          };

          const response = await fetch(`${API_BASE}/projects/projects`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(projectData)
          });

          if (response.ok) {
            toast.success('Project created successfully!');
            fetchCountryDirectorData();
            closeModal();
          } else {
            const error = await response.json();
            toast.error(error.message || 'Failed to create project');
          }
        }
      } else if (modalType === 'addUniversity' || modalType === 'university') {
        // Handle university creation/update
        const universityData = {
          name: formData.name,
          country: formData.country,
          program: formData.program,
          level: formData.level,
          students: formData.students || 0,
          status: formData.status || 'active',
          contact_person: formData.contact_person,
          contact_email: formData.contact_email,
          start_date: formData.start_date
        };

        if (modalMode === 'edit' && selectedUser) {
          // Update existing
          const updated = dashboardData.universities.map(u =>
            u.id === selectedUser.id ? { ...u, ...universityData } : u
          );
          setDashboardData(prev => ({ ...prev, universities: updated }));
          toast.success('University updated successfully!');
        } else {
          // Add new
          const newUniversity = {
            id: Date.now(),
            ...universityData
          };
          setDashboardData(prev => ({
            ...prev,
            universities: [newUniversity, ...prev.universities]
          }));
          toast.success('University added successfully!');
        }
        closeModal();
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('An error occurred. Please try again.');
    }
  };

  // University CRUD handlers
  const handleViewUniversity = (university) => {
    setModalMode('view');
    setModalType('university');
    setSelectedUser(university);
    setFormData(university);
    setShowModal(true);
  };

  const handleEditUniversity = (university) => {
    setModalMode('edit');
    setModalType('university');
    setSelectedUser(university);
    setFormData(university);
    setShowModal(true);
  };

  const handleDeleteUniversity = async (id) => {
    if (!window.confirm('Are you sure you want to delete this university?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      // TODO: Replace with actual API endpoint when implemented
      const universities = dashboardData.universities.filter(u => u.id !== id);
      setDashboardData(prev => ({ ...prev, universities }));
      toast.success('University deleted successfully!');
    } catch (error) {
      console.error('Error deleting university:', error);
      toast.error('Failed to delete university');
    }
  };

  // Report CRUD handlers
  const handleViewReport = async (report) => {
    try {
      const token = localStorage.getItem('adminToken');
      console.log('Viewing report ID:', report.id);

      // Fetch full report details with attachments
      const response = await fetch(`${API_BASE}/leader-reports/reports/${report.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Full API response:', data);
        const fullReport = data.data;
        console.log('Full report data:', fullReport);
        console.log('Attachments array:', fullReport.attachments);
        console.log('Attachments length:', fullReport.attachments?.length);
        console.log('Attachments type:', typeof fullReport.attachments);

        // Log each attachment if present
        if (fullReport.attachments && fullReport.attachments.length > 0) {
          fullReport.attachments.forEach((att, idx) => {
            console.log(`Attachment ${idx}:`, att);
          });
        } else {
          console.log('No attachments found in response');
        }

        setModalMode('view');
        setModalType('viewReport');
        setSelectedUser(fullReport);
        setFormData(fullReport);
        setShowModal(true);
      } else {
        console.error('Failed to fetch report, status:', response.status);
        toast.error('Failed to load report details');
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Error loading report');
    }
  };

  const handleEditReport = (report) => {
    console.log('Editing report:', report);
    setModalMode('edit');
    setModalType('addReport');
    setSelectedUser(report);
    // Properly format the data for editing
    const editData = {
      ...report,
      title: report.title || '',
      description: report.content || report.description || '',
      activity_date: report.activity_date ? new Date(report.activity_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      tags: report.tags || '',
      content: report.content || report.description || ''
    };
    console.log('Setting form data for edit:', editData);
    setFormData(editData);
    setShowModal(true);
  };

  // Project CRUD handlers
  const handleViewProject = async (project) => {
    try {
      const token = localStorage.getItem('adminToken');
      // Fetch full project details
      const response = await fetch(`${API_BASE}/projects/projects/${project.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const fullProject = data.data;
        setModalMode('view');
        setModalType('viewProject');
        setSelectedUser(fullProject);
        setFormData(fullProject);
        setShowModal(true);
      } else {
        toast.error('Failed to load project details');
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      toast.error('Error loading project');
    }
  };

  const handleEditProject = (project) => {
    setModalMode('edit');
    setModalType('addProject');
    setSelectedUser(project);
    setFormData({
      ...project,
      date_started: project.date_started ? new Date(project.date_started).toISOString().split('T')[0] : '',
      expected_completion_date: project.expected_completion_date ? new Date(project.expected_completion_date).toISOString().split('T')[0] : ''
    });
    setShowModal(true);
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/projects/projects/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        fetchCountryDirectorData();
        toast.success('Project deleted successfully!');
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to delete project');
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project');
    }
  };

  const handleDeleteReport = async (id) => {
    if (!window.confirm('Are you sure you want to delete this report?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/leader-reports/reports/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        fetchCountryDirectorData();
        toast.success('Report deleted successfully!');
      } else {
        toast.error('Failed to delete report');
      }
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error('Failed to delete report');
    }
  };

  const StatCard = ({ title, value, icon, color = '#6B8E23' }) => (
    <div className="card border-0 shadow-sm h-100" style={{ borderLeft: `4px solid ${color}` }}>
      <div className="card-body">
        <div className="d-flex align-items-center">
          <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: `${color}20` }}>
            <span className="fs-4" style={{ color }}>{icon}</span>
          </div>
          <div className="ms-3">
            <p className="text-muted mb-1 small">{title}</p>
            <p className="h4 fw-bold mb-0" style={{ color }}>{value}</p>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <PublicLayout user={user} onLogout={onLogout}>
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading Country Directors Dashboard...</p>
          </div>
        </div>
      </PublicLayout>
    );
  }

  const renderOverview = () => (
    <div className="mb-4">
      {/* Welcome Banner */}
      <div className="card border-0 shadow-sm mb-4" style={{ background: 'linear-gradient(135deg, #6B8E23 0%, #58761C 100%)' }}>
        <div className="card-body text-white">
          <h2 className="h3 fw-bold mb-2">
            Welcome, {directorProfile ? `${directorProfile.firstName} ${directorProfile.lastName}` : 'Country Director'}!
          </h2>
          <p className="opacity-90 mb-0">
            {directorProfile?.professionalTitle || 'Country Director'} {directorProfile?.country && `- ${directorProfile.country}`}
          </p>
          {directorProfile?.roleDescription && (
            <p className="opacity-80 mb-0 mt-2 small">{directorProfile.roleDescription}</p>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="row g-4 mb-4">
        <div className="col-md-6 col-lg-3">
          <StatCard
            title="Active Reports"
            value={dashboardData.reports.length}
            icon="📊"
            color="#6B8E23"
          />
        </div>
        <div className="col-md-6 col-lg-3">
          <StatCard
            title="Upcoming Events"
            value={dashboardData.events.filter(e => e.status === 'approved').length}
            icon="📅"
            color="#4A4F4F"
          />
        </div>
        <div className="col-md-6 col-lg-3">
          <StatCard
            title="Active Projects"
            value={dashboardData.projects.filter(p => p.status === 'In Progress' || p.status === 'Started').length}
            icon="🏗️"
            color="#58761C"
          />
        </div>
        <div className="col-md-6 col-lg-3">
          <StatCard
            title="Universities"
            value={dashboardData.universities.length}
            icon="🎓"
            color="#A0A5A5"
          />
        </div>
      </div>

      {/* Recent Activity */}
      <div className="row g-4">
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="h5 fw-medium mb-0">Recent Reports</h3>
                <button className="btn btn-sm btn-primary" onClick={() => openModal('addReport')}>
                  Add Report
                </button>
              </div>
              <div className="list-group">
                {dashboardData.reports && dashboardData.reports.length > 0 ? (
                  dashboardData.reports.slice(0, 3).map(report => (
                    <div key={report.id} className="list-group-item border-0 px-0 py-3">
                      <div className="d-flex justify-content-between">
                        <h4 className="h6 mb-1">{report.title}</h4>
                        <span className={`badge ${report.status === 'approved' ? 'bg-success' : report.status === 'submitted' ? 'bg-primary' : 'bg-warning'}`}>
                          {report.status}
                        </span>
                      </div>
                      <p className="text-muted small mb-1">{report.category_name || 'General'}</p>
                      <p className="text-muted small mb-0">{new Date(report.created_at || report.activity_date).toLocaleDateString()}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-muted text-center py-3">No reports yet</p>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="h5 fw-medium mb-0">Upcoming Events</h3>
                <button className="btn btn-sm btn-primary">
                  Add Event
                </button>
              </div>
              <div className="list-group">
                {dashboardData.events.slice(0, 3).map(event => (
                  <div key={event.id} className="list-group-item border-0 px-0 py-3">
                    <div className="d-flex justify-content-between">
                      <h4 className="h6 mb-1">{event.title}</h4>
                      <span className={`badge ${event.status === 'approved' ? 'bg-success' : 'bg-warning'}`}>
                        {event.status}
                      </span>
                    </div>
                    <p className="text-muted small mb-1">{event.location}</p>
                    <p className="text-muted small mb-0">{event.date}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderReports = () => (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="h4 fw-bold mb-0">Country Reports</h2>
          <button className="btn btn-primary" onClick={() => openModal('addReport')} style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}>
            Add New Report
          </button>
        </div>
        {dashboardData.reports && dashboardData.reports.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData.reports.map(report => (
                  <tr key={report.id}>
                    <td>{report.title}</td>
                    <td>{report.category_name || 'General'}</td>
                    <td>{new Date(report.created_at || report.activity_date).toLocaleDateString()}</td>
                    <td>
                      <span className={`badge ${report.status === 'approved' ? 'bg-success' : report.status === 'submitted' ? 'bg-primary' : report.status === 'under_review' ? 'bg-warning' : 'bg-secondary'}`}>
                        {report.status}
                      </span>
                    </td>
                    <td>
                      <button className="btn btn-sm btn-outline-primary me-1" onClick={() => handleViewReport(report)}>View</button>
                      <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => handleEditReport(report)}>Edit</button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteReport(report.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-5">
            <p className="text-muted">No reports yet. Click "Add New Report" to create your first report.</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderProjects = () => (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="h4 fw-bold mb-0">Regional Projects</h2>
          <button className="btn btn-primary" onClick={() => openModal('addProject')} style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}>
            Add New Project
          </button>
        </div>
        {dashboardData.projects && dashboardData.projects.length > 0 ? (
          <div className="row g-4">
            {dashboardData.projects.map(project => (
              <div key={project.id} className="col-md-6">
                <div className="card h-100 border-0 shadow-sm">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h3 className="h5 fw-bold mb-0">{project.title}</h3>
                      <span className={`badge ${project.status === 'In Progress' ? 'bg-primary' :
                        project.status === 'Completed' ? 'bg-success' :
                          project.status === 'Stalled' ? 'bg-warning' :
                            project.status === 'Abandoned' ? 'bg-danger' :
                              'bg-info'
                        }`}>
                        {project.status}
                      </span>
                    </div>
                    <p className="text-muted small mb-3">{project.description}</p>
                    {project.progress !== null && project.progress !== undefined && (
                      <div className="mb-3">
                        <div className="d-flex justify-content-between mb-1">
                          <span className="small">Progress</span>
                          <span className="small fw-bold">{project.progress}%</span>
                        </div>
                        <div className="progress" style={{ height: '8px' }}>
                          <div
                            className="progress-bar"
                            role="progressbar"
                            style={{ width: `${project.progress}%`, backgroundColor: '#6B8E23' }}
                          ></div>
                        </div>
                      </div>
                    )}
                    <div className="row g-2 mb-3">
                      <div className="col-6">
                        <small className="text-muted">Start Date</small>
                        <p className="mb-0">{new Date(project.date_started).toLocaleDateString()}</p>
                      </div>
                      {project.budget && (
                        <div className="col-6">
                          <small className="text-muted">Budget</small>
                          <p className="mb-0">${parseFloat(project.budget).toLocaleString()}</p>
                        </div>
                      )}
                    </div>
                    <div className="d-flex gap-2">
                      <button className="btn btn-sm btn-outline-primary flex-fill" onClick={() => handleViewProject(project)}>View</button>
                      <button className="btn btn-sm btn-outline-secondary flex-fill" onClick={() => handleEditProject(project)}>Edit</button>
                      <button className="btn btn-sm btn-outline-danger flex-fill" onClick={() => handleDeleteProject(project.id)}>Delete</button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-5">
            <p className="text-muted">No projects yet. Click "Add New Project" to create your first project.</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderUniversities = () => (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="h4 fw-bold mb-0">Universities Offering RT Programs</h2>
          <button
            className="btn btn-primary"
            onClick={() => openModal('addUniversity')}
            style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}
          >
            Add New University
          </button>
        </div>
        {dashboardData.universities && dashboardData.universities.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>University Name</th>
                  <th>Country</th>
                  <th>Program</th>
                  <th>Level</th>
                  <th>Students</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData.universities.map(university => (
                  <tr key={university.id}>
                    <td>{university.name}</td>
                    <td>{university.country}</td>
                    <td>{university.program}</td>
                    <td>{university.level}</td>
                    <td>{university.students || 'N/A'}</td>
                    <td>
                      <span className={`badge ${university.status === 'active' ? 'bg-success' : 'bg-warning'}`}>
                        {university.status || 'active'}
                      </span>
                    </td>
                    <td>
                      <button className="btn btn-sm btn-outline-primary me-1" onClick={() => handleViewUniversity(university)}>View</button>
                      <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => handleEditUniversity(university)}>Edit</button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteUniversity(university.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-5">
            <p className="text-muted">No universities added yet. Click "Add New University" to get started.</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderProfile = () => {
    if (!directorProfile) {
      return (
        <div className="card border-0 shadow-sm">
          <div className="card-body text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading profile...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="row g-4">
        {/* Profile Card */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center">
              {directorProfile.profilePictureUrl || directorProfile.profile_picture_url ? (
                <img
                  src={(directorProfile.profilePictureUrl || directorProfile.profile_picture_url).startsWith('http')
                    ? (directorProfile.profilePictureUrl || directorProfile.profile_picture_url)
                    : `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}${directorProfile.profilePictureUrl || directorProfile.profile_picture_url}`}
                  alt="Profile"
                  className="rounded-circle mb-3"
                  style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              {!directorProfile.profilePictureUrl && !directorProfile.profile_picture_url ? (
                <div
                  className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                  style={{ width: '150px', height: '150px', backgroundColor: '#6B8E23', color: 'white', fontSize: '48px', display: 'flex' }}
                >
                  {directorProfile.firstName?.[0]}{directorProfile.lastName?.[0]}
                </div>
              ) : (
                <div
                  className="rounded-circle mx-auto mb-3 align-items-center justify-content-center"
                  style={{ width: '150px', height: '150px', backgroundColor: '#6B8E23', color: 'white', fontSize: '48px', display: 'none' }}
                >
                  {directorProfile.firstName?.[0]}{directorProfile.lastName?.[0]}
                </div>
              )}
              <button
                className="btn btn-sm btn-outline-primary mb-3"
                onClick={() => openModal('uploadProfilePic')}
                style={{ borderColor: '#6B8E23', color: '#6B8E23' }}
              >
                <FaCamera className="me-2" />
                Change Photo
              </button>
              <h3 className="h5 fw-bold">{directorProfile.firstName} {directorProfile.lastName}</h3>
              <p className="text-muted mb-2">{directorProfile.professionalTitle}</p>
              <span className="badge bg-success mb-3">
                {directorProfile.isActive ? 'Active' : 'Inactive'}
              </span>
              <div className="d-grid gap-2">
                <button className="btn btn-outline-primary" onClick={() => setActiveTab('profile')}>
                  View Full Details
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Details */}
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <h4 className="h5 fw-bold mb-4">Contact Information</h4>
              <div className="row g-3">
                <div className="col-md-6">
                  <p className="text-muted mb-1 small">Email</p>
                  <p className="mb-0">{directorProfile.email}</p>
                </div>
                <div className="col-md-6">
                  <p className="text-muted mb-1 small">Phone</p>
                  <p className="mb-0">{directorProfile.phoneNumber || 'Not set'}</p>
                </div>
                <div className="col-md-6">
                  <p className="text-muted mb-1 small">Country</p>
                  <p className="mb-0">{directorProfile.country || 'Not set'}</p>
                </div>
                <div className="col-md-6">
                  <p className="text-muted mb-1 small">City/Region</p>
                  <p className="mb-0">{directorProfile.cityRegion || 'Not set'}</p>
                </div>
                {directorProfile.linkedinProfile && (
                  <div className="col-md-12">
                    <p className="text-muted mb-1 small">LinkedIn</p>
                    <a href={directorProfile.linkedinProfile} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                      {directorProfile.linkedinProfile}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>

          {directorProfile.roleDescription && (
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body">
                <h4 className="h5 fw-bold mb-3">Role Description</h4>
                <p className="mb-0">{directorProfile.roleDescription}</p>
              </div>
            </div>
          )}

          {directorProfile.professionalBackground && (
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body">
                <h4 className="h5 fw-bold mb-3">Professional Background</h4>
                <p className="mb-0 whitespace-pre-wrap">{directorProfile.professionalBackground}</p>
              </div>
            </div>
          )}

          {directorProfile.areasOfExpertise && (
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body">
                <h4 className="h5 fw-bold mb-3">Areas of Expertise</h4>
                <p className="mb-0">{directorProfile.areasOfExpertise}</p>
              </div>
            </div>
          )}

          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h4 className="h5 fw-bold mb-3">Additional Information</h4>
              <div className="row g-3">
                {directorProfile.languagesSpoken && (
                  <div className="col-md-6">
                    <p className="text-muted mb-1 small">Languages</p>
                    <p className="mb-0">{directorProfile.languagesSpoken}</p>
                  </div>
                )}
                {directorProfile.appointmentDate && (
                  <div className="col-md-6">
                    <p className="text-muted mb-1 small">Appointment Date</p>
                    <p className="mb-0">{new Date(directorProfile.appointmentDate).toLocaleDateString()}</p>
                  </div>
                )}
                {directorProfile.departmentUnit && (
                  <div className="col-md-6">
                    <p className="text-muted mb-1 small">Department</p>
                    <p className="mb-0">{directorProfile.departmentUnit}</p>
                  </div>
                )}
                {directorProfile.reportingStructure && (
                  <div className="col-md-6">
                    <p className="text-muted mb-1 small">Reports To</p>
                    <p className="mb-0">{directorProfile.reportingStructure}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview': return renderOverview();
      case 'profile': return renderProfile();
      case 'reports': return renderReports();
      case 'projects': return renderProjects();
      case 'universities': return renderUniversities();
      default: return renderOverview();
    }
  };

  return (
    <PublicLayout user={user} onLogout={onLogout}>
      <style>{`
        .country-tabs-scroll {
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .country-tabs-scroll::-webkit-scrollbar {
          display: none;
        }
        .country-tabs-scroll .btn {
          white-space: nowrap;
        }
      `}</style>
      <div className="bg-light py-4">
        <div className="container">
          {/* Navigation Tabs */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <div className="d-flex flex-nowrap gap-2 overflow-auto pb-1 country-tabs-scroll">
                {[
                  { id: 'overview', name: 'Overview', icon: '📊' },
                  { id: 'profile', name: 'My Profile', icon: '👤' },
                  { id: 'reports', name: 'Reports', icon: '📝' },
                  { id: 'projects', name: 'Projects', icon: '🏗️' },
                  { id: 'universities', name: 'Universities Offering RT Programs', icon: '🎓' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`btn px-3 py-2 rounded-pill ${activeTab === tab.id ? 'btn-primary' : 'btn-outline-secondary'}`}
                  >
                    <span className="me-1">{tab.icon}</span>
                    <span>{tab.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Content Area */}
          {renderTabContent()}

          {/* Modal for adding reports/projects */}
          {showModal && (
            <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="modal-dialog modal-lg">
                <div className="modal-content">
                  <div className="modal-header" style={{ backgroundColor: '#6B8E23', color: 'white' }}>
                    <h5 className="modal-title">
                      {modalType === 'uploadProfilePic' ? 'Upload Profile Picture' :
                        modalType === 'addReport' ? (modalMode === 'edit' ? 'Edit Report' : 'Create New Report') :
                          modalType === 'viewReport' ? 'View Report Details' :
                            modalType === 'addProject' ? (modalMode === 'edit' ? 'Edit Project' : 'Create New Project') :
                              modalType === 'viewProject' ? 'View Project Details' :
                                modalType === 'addUniversity' ? 'Add New University' :
                                  modalType === 'university' ? (modalMode === 'view' ? 'University Details' : 'Edit University') :
                                    'Create New Project'}
                    </h5>
                    <button type="button" className="btn-close btn-close-white" onClick={closeModal}></button>
                  </div>
                  <div className="modal-body">
                    {modalType === 'viewReport' ? (
                      // View Report Mode
                      <div>
                        <div className="mb-4">
                          <h6 className="text-muted mb-2">Report Title</h6>
                          <p className="h5">{formData.title}</p>
                        </div>
                        <div className="row mb-4">
                          <div className="col-md-6">
                            <h6 className="text-muted mb-2">Category</h6>
                            <p>{formData.category_name || 'General'}</p>
                          </div>
                          <div className="col-md-6">
                            <h6 className="text-muted mb-2">Activity Date</h6>
                            <p>{formData.activity_date ? new Date(formData.activity_date).toLocaleDateString() : 'N/A'}</p>
                          </div>
                        </div>
                        <div className="row mb-4">
                          <div className="col-md-6">
                            <h6 className="text-muted mb-2">Status</h6>
                            <span className={`badge ${formData.status === 'approved' ? 'bg-success' : formData.status === 'submitted' ? 'bg-primary' : formData.status === 'under_review' ? 'bg-warning' : 'bg-secondary'}`}>
                              {formData.status}
                            </span>
                          </div>
                          <div className="col-md-6">
                            <h6 className="text-muted mb-2">Created Date</h6>
                            <p>{formData.created_at ? new Date(formData.created_at).toLocaleDateString() : 'N/A'}</p>
                          </div>
                        </div>
                        <div className="mb-4">
                          <h6 className="text-muted mb-2">Report Content</h6>
                          <div className="p-3 bg-light rounded" style={{ whiteSpace: 'pre-wrap' }}>
                            {formData.content || formData.description || 'No content available'}
                          </div>
                        </div>
                        {formData.tags && (
                          <div className="mb-4">
                            <h6 className="text-muted mb-2">Tags</h6>
                            <p>{formData.tags}</p>
                          </div>
                        )}
                        {/* Debug: Show attachments info */}
                        {console.log('Checking attachments in view:', formData.attachments)}
                        {formData.attachments && formData.attachments.length > 0 ? (
                          <div className="mb-4">
                            <h6 className="text-muted mb-2">Attached Files ({formData.attachments.length})</h6>
                            <div className="list-group">
                              {formData.attachments.map((attachment, index) => (
                                <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
                                  <div>
                                    <i className="bi bi-file-pdf text-danger me-2"></i>
                                    <span>{attachment.original_filename || attachment.filename || 'Unnamed file'}</span>
                                    <small className="text-muted ms-2">({(attachment.file_size / 1024).toFixed(2)} KB)</small>
                                  </div>
                                  <a
                                    href={`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}${attachment.file_path}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-primary"
                                  >
                                    <i className="bi bi-download me-1"></i>Download
                                  </a>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="mb-4">
                            <h6 className="text-muted mb-2">Attached Files</h6>
                            <p className="text-muted fst-italic">No files attached to this report</p>
                          </div>
                        )}
                        <div className="d-flex justify-content-end">
                          <button type="button" className="btn btn-secondary me-2" onClick={closeModal}>Close</button>
                          <button type="button" className="btn btn-primary" onClick={() => handleEditReport(formData)} style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}>Edit Report</button>
                        </div>
                      </div>
                    ) : modalType === 'viewProject' ? (
                      // View Project Mode
                      <div>
                        <div className="mb-4">
                          <h6 className="text-muted mb-2">Project Title</h6>
                          <p className="h5">{formData.title}</p>
                        </div>
                        <div className="mb-4">
                          <h6 className="text-muted mb-2">Description</h6>
                          <div className="p-3 bg-light rounded" style={{ whiteSpace: 'pre-wrap' }}>
                            {formData.description || 'No description available'}
                          </div>
                        </div>
                        <div className="row mb-4">
                          <div className="col-md-6">
                            <h6 className="text-muted mb-2">Status</h6>
                            <span className={`badge ${formData.status === 'In Progress' ? 'bg-primary' :
                              formData.status === 'Completed' ? 'bg-success' :
                                formData.status === 'Stalled' ? 'bg-warning' :
                                  formData.status === 'Abandoned' ? 'bg-danger' :
                                    'bg-info'
                              }`}>
                              {formData.status}
                            </span>
                          </div>
                          <div className="col-md-6">
                            <h6 className="text-muted mb-2">Progress</h6>
                            <div>
                              <div className="progress" style={{ height: '20px' }}>
                                <div
                                  className="progress-bar"
                                  role="progressbar"
                                  style={{ width: `${formData.progress || 0}%`, backgroundColor: '#6B8E23' }}
                                >
                                  {formData.progress || 0}%
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="row mb-4">
                          <div className="col-md-6">
                            <h6 className="text-muted mb-2">Date Started</h6>
                            <p>{formData.date_started ? new Date(formData.date_started).toLocaleDateString() : 'N/A'}</p>
                          </div>
                          <div className="col-md-6">
                            <h6 className="text-muted mb-2">Expected Completion</h6>
                            <p>{formData.expected_completion_date ? new Date(formData.expected_completion_date).toLocaleDateString() : 'Not set'}</p>
                          </div>
                        </div>
                        <div className="row mb-4">
                          <div className="col-md-6">
                            <h6 className="text-muted mb-2">Budget</h6>
                            <p>{formData.budget ? `$${parseFloat(formData.budget).toLocaleString()}` : 'Not set'}</p>
                          </div>
                          <div className="col-md-6">
                            <h6 className="text-muted mb-2">Target Beneficiaries</h6>
                            <p>{formData.target_beneficiaries || 'Not set'}</p>
                          </div>
                        </div>
                        {formData.region && (
                          <div className="mb-4">
                            <h6 className="text-muted mb-2">Region</h6>
                            <p>{formData.region}</p>
                          </div>
                        )}
                        {formData.objectives && (
                          <div className="mb-4">
                            <h6 className="text-muted mb-2">Project Objectives</h6>
                            <div className="p-3 bg-light rounded" style={{ whiteSpace: 'pre-wrap' }}>
                              {formData.objectives}
                            </div>
                          </div>
                        )}
                        <div className="d-flex justify-content-end">
                          <button type="button" className="btn btn-secondary me-2" onClick={closeModal}>Close</button>
                          <button type="button" className="btn btn-primary" onClick={() => handleEditProject(formData)} style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}>Edit Project</button>
                        </div>
                      </div>
                    ) : (
                      <form onSubmit={handleFormSubmit}>
                        {modalType === 'uploadProfilePic' ? (
                          <>
                            <div className="mb-3">
                              <label className="form-label fw-bold">Select Profile Picture *</label>
                              <input
                                type="file"
                                className="form-control"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    if (file.size > 5 * 1024 * 1024) {
                                      toast.error('File size must be less than 5MB');
                                      e.target.value = '';
                                      return;
                                    }
                                    if (!file.type.startsWith('image/')) {
                                      toast.error('Only image files are allowed');
                                      e.target.value = '';
                                      return;
                                    }
                                    setProfilePictureFile(file);
                                    toast.success(`Selected: ${file.name}`);
                                  }
                                }}
                                required
                              />
                              <small className="text-muted">Max size: 5MB. Formats: JPG, PNG, GIF</small>
                            </div>
                            {profilePictureFile && (
                              <div className="alert alert-success">
                                <strong>Selected:</strong> {profilePictureFile.name}
                              </div>
                            )}
                          </>
                        ) : modalType === 'addReport' ? (
                          <>
                            <div className="mb-3">
                              <label className="form-label fw-bold">Report Title *</label>
                              <input
                                type="text"
                                className="form-control"
                                name="title"
                                value={formData.title || ''}
                                onChange={handleInputChange}
                                required
                                placeholder="e.g., Q4 2024 Country Report"
                              />
                            </div>
                            <div className="mb-3">
                              <label className="form-label fw-bold">Activity Date *</label>
                              <input
                                type="date"
                                className="form-control"
                                name="activity_date"
                                value={formData.activity_date || new Date().toISOString().split('T')[0]}
                                onChange={handleInputChange}
                                required
                              />
                            </div>
                            <div className="mb-3">
                              <label className="form-label fw-bold">Report Content *</label>
                              <textarea
                                className="form-control"
                                name="description"
                                value={formData.description || formData.content || ''}
                                onChange={handleInputChange}
                                required
                                rows="6"
                                placeholder="Provide detailed information about your report..."
                              ></textarea>
                            </div>
                            <div className="mb-3">
                              <label className="form-label fw-bold">Tags (optional)</label>
                              <input
                                type="text"
                                className="form-control"
                                name="tags"
                                value={formData.tags || ''}
                                onChange={handleInputChange}
                                placeholder="e.g., financial, quarterly, budget"
                              />
                              <small className="text-muted">Separate tags with commas</small>
                            </div>
                            <div className="mb-3">
                              <label className="form-label fw-bold"><FaUpload className="me-2" />Upload PDF Report (optional)</label>
                              <input
                                type="file"
                                className="form-control"
                                accept="application/pdf"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    if (file.size > 10 * 1024 * 1024) {
                                      toast.error('PDF file size must be less than 10MB');
                                      e.target.value = '';
                                      return;
                                    }
                                    if (file.type !== 'application/pdf') {
                                      toast.error('Only PDF files are allowed');
                                      e.target.value = '';
                                      return;
                                    }
                                    setFormData(prev => ({ ...prev, pdfFile: file }));
                                    toast.success(`Selected: ${file.name}`);
                                  }
                                }}
                              />
                              <small className="text-muted">Max size: 10MB. PDF format only</small>
                              {formData.pdfFile && (
                                <div className="alert alert-success mt-2">
                                  <strong>Selected:</strong> {formData.pdfFile.name}
                                </div>
                              )}
                            </div>
                          </>
                        ) : modalType === 'addProject' ? (
                          <>
                            <div className="mb-3">
                              <label className="form-label fw-bold">Project Title *</label>
                              <input
                                type="text"
                                className="form-control"
                                name="title"
                                value={formData.title || ''}
                                onChange={handleInputChange}
                                required
                                placeholder="e.g., Rural Healthcare Initiative"
                              />
                            </div>
                            <div className="mb-3">
                              <label className="form-label fw-bold">Description *</label>
                              <textarea
                                className="form-control"
                                name="description"
                                value={formData.description || ''}
                                onChange={handleInputChange}
                                required
                                rows="4"
                                placeholder="Provide detailed description of the project..."
                              ></textarea>
                            </div>
                            <div className="row">
                              <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">Date Started *</label>
                                <input
                                  type="date"
                                  className="form-control"
                                  name="date_started"
                                  value={formData.date_started || ''}
                                  onChange={handleInputChange}
                                  required
                                />
                              </div>
                              <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">Expected Completion Date</label>
                                <input
                                  type="date"
                                  className="form-control"
                                  name="expected_completion_date"
                                  value={formData.expected_completion_date || ''}
                                  onChange={handleInputChange}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">Status *</label>
                                <select
                                  className="form-select"
                                  name="status"
                                  value={formData.status || 'Started'}
                                  onChange={handleInputChange}
                                  required
                                >
                                  <option value="Started">Started</option>
                                  <option value="In Progress">In Progress</option>
                                  <option value="Completed">Completed</option>
                                  <option value="Stalled">Stalled</option>
                                  <option value="Abandoned">Abandoned</option>
                                </select>
                              </div>
                              <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">Region</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="region"
                                  value={formData.region || ''}
                                  onChange={handleInputChange}
                                  placeholder="e.g., Central Region"
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">Budget (USD)</label>
                                <input
                                  type="number"
                                  className="form-control"
                                  name="budget"
                                  value={formData.budget || ''}
                                  onChange={handleInputChange}
                                  placeholder="0.00"
                                  step="0.01"
                                />
                              </div>
                              <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">Target Beneficiaries</label>
                                <input
                                  type="number"
                                  className="form-control"
                                  name="target_beneficiaries"
                                  value={formData.target_beneficiaries || ''}
                                  onChange={handleInputChange}
                                  placeholder="Number of people to benefit"
                                />
                              </div>
                            </div>
                            <div className="mb-3">
                              <label className="form-label fw-bold">Project Objectives</label>
                              <textarea
                                className="form-control"
                                name="objectives"
                                value={formData.objectives || ''}
                                onChange={handleInputChange}
                                rows="3"
                                placeholder="List the key objectives of this project..."
                              ></textarea>
                            </div>
                          </>
                        ) : (modalType === 'addUniversity' || modalType === 'university') ? (
                          <>
                            <div className="row">
                              <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">University Name *</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="name"
                                  value={formData.name || ''}
                                  onChange={handleInputChange}
                                  required
                                  disabled={modalMode === 'view'}
                                />
                              </div>
                              <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">Country *</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="country"
                                  value={formData.country || ''}
                                  onChange={handleInputChange}
                                  required
                                  disabled={modalMode === 'view'}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">Program Name *</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="program"
                                  value={formData.program || ''}
                                  onChange={handleInputChange}
                                  placeholder="e.g., Respiratory Therapy Diploma"
                                  required
                                  disabled={modalMode === 'view'}
                                />
                              </div>
                              <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">Level *</label>
                                <select
                                  className="form-select"
                                  name="level"
                                  value={formData.level || ''}
                                  onChange={handleInputChange}
                                  required
                                  disabled={modalMode === 'view'}
                                >
                                  <option value="">Select Level</option>
                                  <option value="Certificate">Certificate</option>
                                  <option value="Diploma">Diploma</option>
                                  <option value="Degree">Degree</option>
                                  <option value="Masters">Masters</option>
                                  <option value="PhD">PhD</option>
                                </select>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">Number of Students</label>
                                <input
                                  type="number"
                                  className="form-control"
                                  name="students"
                                  value={formData.students || ''}
                                  onChange={handleInputChange}
                                  placeholder="0"
                                  disabled={modalMode === 'view'}
                                />
                              </div>
                              <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">Status</label>
                                <select
                                  className="form-select"
                                  name="status"
                                  value={formData.status || 'active'}
                                  onChange={handleInputChange}
                                  disabled={modalMode === 'view'}
                                >
                                  <option value="active">Active</option>
                                  <option value="planning">Planning</option>
                                  <option value="inactive">Inactive</option>
                                </select>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">Contact Person</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="contact_person"
                                  value={formData.contact_person || ''}
                                  onChange={handleInputChange}
                                  disabled={modalMode === 'view'}
                                />
                              </div>
                              <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">Contact Email</label>
                                <input
                                  type="email"
                                  className="form-control"
                                  name="contact_email"
                                  value={formData.contact_email || ''}
                                  onChange={handleInputChange}
                                  disabled={modalMode === 'view'}
                                />
                              </div>
                            </div>
                            <div className="mb-3">
                              <label className="form-label fw-bold">Program Start Date</label>
                              <input
                                type="date"
                                className="form-control"
                                name="start_date"
                                value={formData.start_date || ''}
                                onChange={handleInputChange}
                                disabled={modalMode === 'view'}
                              />
                            </div>
                          </>
                        ) : null}
                        <div className="d-flex justify-content-end gap-2 mt-4">
                          {modalMode !== 'view' && (
                            <>
                              <button type="button" className="btn btn-secondary" onClick={closeModal}>
                                Cancel
                              </button>
                              <button type="submit" className="btn btn-primary" style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}>
                                {modalType === 'uploadProfilePic' ? 'Upload Picture' :
                                  modalType === 'addReport' ? (modalMode === 'edit' ? 'Update Report' : 'Create Report') :
                                    modalType === 'addProject' ? (modalMode === 'edit' ? 'Update Project' : 'Create Project') :
                                      modalType === 'addUniversity' ? 'Add University' :
                                        modalType === 'university' ? 'Update University' :
                                          'Create'}
                              </button>
                            </>
                          )}
                          {modalMode === 'view' && modalType !== 'viewReport' && modalType !== 'viewProject' && (
                            <button type="button" className="btn btn-primary" onClick={closeModal} style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}>
                              Close
                            </button>
                          )}
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </PublicLayout>
  );
};

export default CountryDirectorsDashboard;